import { PERMISSIONS } from './permissions.js';
/** Schützbare Bereiche der Anwendung; Zeilen der Berechtigungsmatrix. */
export const PERMISSION_RESOURCES = [
    'OPERATIONS',
    'RETURNS',
    'PICKING',
    'INVENTORY_COUNTS',
    'DELIVERY_TOURS',
    'REUSABLE_CONTAINERS',
    'ELECTRONIC_INVOICES',
    'INTRODUCTION',
    'FEATURES',
    'GLOBAL_SEARCH',
    'VAT_VALIDATION',
    'DUNNING',
    'CUSTOMER_PORTAL',
    'CUSTOMERS',
    'PARTNER_RISK',
    'PRICING',
    'PRODUCTS',
    'BATCHES',
    'DOCUMENTS',
    'DOCUMENT_CUSTOMER_HEADERS',
    'DMS',
    'PROCUREMENT',
    'ACCOUNTING',
    'ANALYTICS',
    'EMPLOYEE_PERFORMANCE',
    'SETTINGS',
    'USERS',
    'WHATSAPP_MESSAGES',
    'WHATSAPP_TEMPLATES',
    'WHATSAPP_CONFIG',
    'TELEPHONY',
    'QUESTIONNAIRES',
    'PRODUCT_SPECIFICATIONS',
    'FEEDBACK',
    'PRODUCT_DEVELOPMENT',
    'RAW_MATERIAL',
    'RECIPES',
    'DECLARATIONS',
    'ANALYSIS',
    'MANUFACTURING',
    'TRACEABILITY',
    'MRP',
    'LABEL_MANAGEMENT',
    'CHANGE_CONTROL',
    'NEWSLETTER',
    'CUSTOM_FIELDS',
    'TRANSLATIONS',
    'SETUP',
    'IMPORTS',
    'BANKING',
    'SALES_ASSISTANT',
    'SALES_RULES',
    'SALES_ANALYTICS',
    'CONTACT_PREFERENCES',
    'BACKUPS',
];
/** Spalten der Berechtigungsmatrix. */
export const PERMISSION_ACTIONS = [
    'CREATE',
    'READ',
    'UPDATE',
    'DELETE',
    'EXPORT',
    'APPROVE',
    'RELEASE',
    'RESERVE',
    'CONSUME',
    'COMPLETE',
    'SIMULATE',
    'BLOCK',
    'SEND',
    'ASSIGN',
    'MANAGE',
    'MANAGE_TEMPLATES',
    'ADMIN',
    'USE',
    'EXECUTE',
    'VIEW',
    'SET_LOCK',
    'OVERRIDE_LOCK',
    'VIEW_OWN_LOGS',
    'VIEW_ALL_LOGS',
    'VIEW_AI_SUMMARY',
    'EDIT_AI_SUMMARY',
    'MANAGE_SETTINGS',
    'VIEW_OWN_RUNS',
    'VIEW_ALL_RUNS',
    'EXPORT_RESULTS',
    'REVIEW',
    'PUBLISH',
    'MANAGE_SCHEMA',
    'MANAGE_PDF_TEMPLATES',
    'ARCHIVE',
    'VIEW_COSTS',
    'RUN',
    'CONFIGURE',
    'CONVERT_PURCHASE',
    'CONVERT_PRODUCTION',
    'IMPORT',
    'ROLLBACK',
    'MANAGE_FIELDS',
    'MANAGE_GROUPS',
    'READ_CONFIDENTIAL',
    'WRITE_CONFIDENTIAL',
    'VIEW_OWN_TASKS',
    'VIEW_TEAM_TASKS',
    'DOCUMENT_CONTACT',
    'SNOOZE',
    'EXCLUDE',
    'GENERATE_AI',
    'VIEW_TEAM',
    'VIEW_EMPLOYEE_DETAILS',
    'VIEW_AUDIT',
    'CREATE_SETTINGS',
    'CREATE_MASTER_DATA',
    'CREATE_FULL',
    'DOWNLOAD',
    'RESTORE',
    'RESTORE_DRY_RUN',
    'VIEW_DEPLOYMENT',
    'DELETE_DETAILS',
    'EDIT_PRICES',
    'CANCEL',
];
/**
 * Übersetzung der normalisierten Matrix in die Permission-Strings, die JWT und
 * Routen-Guards auswerten. Nicht belegte Kombinationen gelten als nicht vergeben.
 */
const MATRIX_TO_PERMISSIONS = {
    OPERATIONS: {
        READ: [PERMISSIONS.OPERATIONS_READ],
        CONFIGURE: [PERMISSIONS.OPERATIONS_READ, PERMISSIONS.OPERATIONS_CONFIGURE],
    },
    RETURNS: {
        READ: [PERMISSIONS.RETURNS_READ],
        CREATE: [PERMISSIONS.RETURNS_WRITE],
        UPDATE: [PERMISSIONS.RETURNS_WRITE],
        APPROVE: [PERMISSIONS.RETURNS_APPROVE],
        RELEASE: [PERMISSIONS.RETURNS_INSPECT],
        EXECUTE: [PERMISSIONS.RETURNS_REFUND],
    },
    PICKING: {
        READ: [PERMISSIONS.PICKING_READ],
        CREATE: [PERMISSIONS.PICKING_WRITE],
        UPDATE: [PERMISSIONS.PICKING_WRITE],
        OVERRIDE_LOCK: [PERMISSIONS.PICKING_OVERRIDE],
    },
    INVENTORY_COUNTS: {
        READ: [PERMISSIONS.INVENTORY_READ],
        CREATE: [PERMISSIONS.INVENTORY_WRITE],
        UPDATE: [PERMISSIONS.INVENTORY_WRITE],
        COMPLETE: [PERMISSIONS.INVENTORY_CLOSE],
    },
    DELIVERY_TOURS: {
        READ: [PERMISSIONS.TOURS_READ],
        CREATE: [PERMISSIONS.TOURS_WRITE],
        UPDATE: [PERMISSIONS.TOURS_WRITE],
        USE: [PERMISSIONS.TOURS_DRIVE],
    },
    REUSABLE_CONTAINERS: {
        READ: [PERMISSIONS.CONTAINERS_READ],
        CREATE: [PERMISSIONS.CONTAINERS_WRITE],
        UPDATE: [PERMISSIONS.CONTAINERS_WRITE],
        APPROVE: [PERMISSIONS.CONTAINERS_CORRECT],
    },
    ELECTRONIC_INVOICES: {
        READ: [PERMISSIONS.EINVOICE_READ],
        EXPORT: [PERMISSIONS.EINVOICE_EXPORT],
        CREATE: [PERMISSIONS.EINVOICE_IMPORT],
        CONFIGURE: [PERMISSIONS.EINVOICE_SETTINGS],
    },
    INTRODUCTION: {
        MANAGE: [PERMISSIONS.INTRODUCTION_MANAGE],
        DELETE: [PERMISSIONS.INTRODUCTION_MANAGE, PERMISSIONS.INTRODUCTION_RESET],
    },
    FEATURES: {
        READ: [PERMISSIONS.FEATURES_READ],
        MANAGE: [PERMISSIONS.FEATURES_READ, PERMISSIONS.FEATURES_MANAGE],
    },
    GLOBAL_SEARCH: {
        USE: [PERMISSIONS.GLOBAL_SEARCH_USE],
    },
    VAT_VALIDATION: {
        READ: [PERMISSIONS.VAT_VALIDATION_READ],
        EXECUTE: [PERMISSIONS.VAT_VALIDATION_READ, PERMISSIONS.VAT_VALIDATION_EXECUTE],
        MANAGE: [PERMISSIONS.VAT_VALIDATION_READ, PERMISSIONS.VAT_VALIDATION_MANAGE],
    },
    DUNNING: {
        READ: [PERMISSIONS.DUNNING_READ],
        CREATE: [PERMISSIONS.DUNNING_READ, PERMISSIONS.DUNNING_PREPARE],
        APPROVE: [PERMISSIONS.DUNNING_READ, PERMISSIONS.DUNNING_APPROVE],
        SEND: [PERMISSIONS.DUNNING_READ, PERMISSIONS.DUNNING_SEND],
        DELETE: [PERMISSIONS.DUNNING_READ, PERMISSIONS.DUNNING_CANCEL],
        MANAGE_SETTINGS: [PERMISSIONS.DUNNING_READ, PERMISSIONS.DUNNING_MANAGE_SETTINGS],
    },
    CUSTOMER_PORTAL: {
        READ: [PERMISSIONS.PORTAL_ACTIVITY_READ],
        REVIEW: [PERMISSIONS.PORTAL_ORDERS_REVIEW],
        MANAGE: [PERMISSIONS.PORTAL_ACCESS_MANAGE],
    },
    CUSTOMERS: {
        CREATE: [PERMISSIONS.CUSTOMER_WRITE],
        READ: [PERMISSIONS.CUSTOMER_READ],
        UPDATE: [PERMISSIONS.CUSTOMER_WRITE],
        DELETE: [PERMISSIONS.CUSTOMER_DELETE],
        DELETE_DETAILS: [PERMISSIONS.CUSTOMER_DETAILS_DELETE],
        EDIT_PRICES: [PERMISSIONS.CUSTOMER_PRICING_WRITE],
    },
    PARTNER_RISK: {
        VIEW: [PERMISSIONS.PARTNER_RISK_VIEW],
        // Wer sperrt, muss die Einstufung zwangsläufig auch sehen dürfen.
        SET_LOCK: [PERMISSIONS.PARTNER_RISK_VIEW, PERMISSIONS.PARTNER_RISK_SET_LOCK],
        OVERRIDE_LOCK: [
            PERMISSIONS.PARTNER_RISK_VIEW,
            PERMISSIONS.PARTNER_RISK_SET_LOCK,
            PERMISSIONS.PARTNER_RISK_OVERRIDE_LOCK,
        ],
    },
    PRICING: {
        READ: [PERMISSIONS.PRICING_READ],
        VIEW_COSTS: [PERMISSIONS.PRICING_COSTS],
        UPDATE: [PERMISSIONS.PRICING_WRITE],
        APPROVE: [PERMISSIONS.PRICING_APPROVE],
    },
    PRODUCTS: {
        CREATE: [PERMISSIONS.PRODUCT_WRITE],
        READ: [PERMISSIONS.PRODUCT_READ],
        UPDATE: [PERMISSIONS.PRODUCT_WRITE],
        DELETE: [PERMISSIONS.PRODUCT_DELETE],
    },
    BATCHES: {
        CREATE: [PERMISSIONS.INVENTORY_WRITE],
        READ: [PERMISSIONS.INVENTORY_READ],
        UPDATE: [PERMISSIONS.INVENTORY_WRITE],
    },
    DOCUMENTS: {
        CREATE: [PERMISSIONS.DOCUMENT_WRITE],
        READ: [PERMISSIONS.DOCUMENT_READ],
        UPDATE: [PERMISSIONS.DOCUMENT_WRITE],
        DELETE: [PERMISSIONS.DOCUMENT_DELETE],
        CANCEL: [PERMISSIONS.INVOICE_CANCEL],
        EDIT_PRICES: [PERMISSIONS.DOCUMENT_PRICE_OVERRIDE],
        EXPORT: [PERMISSIONS.DOCUMENT_SEND],
        APPROVE: [PERMISSIONS.INVOICE_POST],
    },
    DOCUMENT_CUSTOMER_HEADERS: {
        READ: [PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_READ],
        READ_CONFIDENTIAL: [
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_READ,
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_READ_INTERNAL,
        ],
        UPDATE: [
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_READ,
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_EDIT_VALUES,
        ],
        EXECUTE: [
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_READ,
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_REFRESH_VALUES,
        ],
        CONFIGURE: [
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_READ,
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_CUSTOMIZE_LAYOUT,
        ],
        MANAGE_TEMPLATES: [
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_READ,
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_MANAGE_LAYOUTS,
        ],
        PUBLISH: [
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_READ,
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_CONFIGURE_EXTERNAL,
        ],
        VIEW_AUDIT: [
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_READ,
            PERMISSIONS.DOCUMENT_CUSTOMER_HEADER_AUDIT_READ,
        ],
    },
    DMS: {
        CREATE: [PERMISSIONS.DMS_WRITE],
        READ: [PERMISSIONS.DMS_READ],
        UPDATE: [PERMISSIONS.DMS_WRITE],
        DELETE: [PERMISSIONS.DMS_DELETE],
    },
    PROCUREMENT: {
        CREATE: [PERMISSIONS.PURCHASE_WRITE],
        READ: [PERMISSIONS.PURCHASE_READ],
        UPDATE: [PERMISSIONS.PURCHASE_WRITE],
        DELETE: [PERMISSIONS.PURCHASE_WRITE],
        APPROVE: [PERMISSIONS.PURCHASE_INVOICE_APPROVE],
    },
    ACCOUNTING: {
        READ: [PERMISSIONS.PURCHASE_READ],
        UPDATE: [PERMISSIONS.PURCHASE_WRITE],
        EXPORT: [PERMISSIONS.EXPORT_DATEV, PERMISSIONS.EXPORT_INTRASTAT],
    },
    ANALYTICS: {
        CREATE: [PERMISSIONS.REPORT_WRITE],
        READ: [PERMISSIONS.REPORT_READ],
        UPDATE: [PERMISSIONS.REPORT_WRITE],
        DELETE: [PERMISSIONS.REPORT_WRITE],
    },
    EMPLOYEE_PERFORMANCE: {
        VIEW: [PERMISSIONS.EMPLOYEE_PERFORMANCE_VIEW],
    },
    SETTINGS: {
        READ: [PERMISSIONS.SETTINGS_READ],
        UPDATE: [PERMISSIONS.SETTINGS_WRITE],
        MANAGE_TEMPLATES: [PERMISSIONS.SETTINGS_MANAGE_TEMPLATES],
        ADMIN: [PERMISSIONS.SETTINGS_READ, PERMISSIONS.SETTINGS_WRITE],
    },
    USERS: {
        CREATE: [PERMISSIONS.USER_WRITE],
        READ: [PERMISSIONS.USER_READ],
        UPDATE: [PERMISSIONS.USER_WRITE],
        DELETE: [PERMISSIONS.USER_WRITE],
        ADMIN: [PERMISSIONS.USER_WRITE, PERMISSIONS.SETTINGS_WRITE],
    },
    WHATSAPP_MESSAGES: {
        READ: [PERMISSIONS.WHATSAPP_READ],
        SEND: [PERMISSIONS.WHATSAPP_READ, PERMISSIONS.WHATSAPP_SEND],
        ASSIGN: [PERMISSIONS.WHATSAPP_READ, PERMISSIONS.WHATSAPP_ASSIGN],
    },
    WHATSAPP_TEMPLATES: {
        READ: [PERMISSIONS.WHATSAPP_READ],
        MANAGE: [PERMISSIONS.WHATSAPP_TEMPLATE_MANAGE],
    },
    WHATSAPP_CONFIG: {
        ADMIN: [PERMISSIONS.WHATSAPP_CONFIG, PERMISSIONS.SETTINGS_WRITE],
    },
    TELEPHONY: {
        USE: [PERMISSIONS.TELEPHONY_USE],
        VIEW_OWN_LOGS: [PERMISSIONS.TELEPHONY_VIEW_OWN_LOGS],
        VIEW_ALL_LOGS: [PERMISSIONS.TELEPHONY_VIEW_OWN_LOGS, PERMISSIONS.TELEPHONY_VIEW_ALL_LOGS],
        VIEW_AI_SUMMARY: [PERMISSIONS.TELEPHONY_VIEW_AI_SUMMARY],
        // Wer bearbeiten darf, muss den Inhalt zwangsläufig auch sehen dürfen.
        EDIT_AI_SUMMARY: [PERMISSIONS.TELEPHONY_VIEW_AI_SUMMARY, PERMISSIONS.TELEPHONY_EDIT_AI_SUMMARY],
        MANAGE_SETTINGS: [PERMISSIONS.TELEPHONY_MANAGE_SETTINGS, PERMISSIONS.SETTINGS_WRITE],
    },
    QUESTIONNAIRES: {
        USE: [PERMISSIONS.QUESTIONNAIRE_USE],
        VIEW_OWN_RUNS: [PERMISSIONS.QUESTIONNAIRE_VIEW_OWN_RUNS],
        VIEW_ALL_RUNS: [
            PERMISSIONS.QUESTIONNAIRE_VIEW_OWN_RUNS,
            PERMISSIONS.QUESTIONNAIRE_VIEW_ALL_RUNS,
        ],
        MANAGE_TEMPLATES: [PERMISSIONS.QUESTIONNAIRE_MANAGE_TEMPLATES],
        EXPORT_RESULTS: [PERMISSIONS.QUESTIONNAIRE_EXPORT_RESULTS],
        MANAGE_SETTINGS: [PERMISSIONS.QUESTIONNAIRE_MANAGE_SETTINGS],
    },
    PRODUCT_SPECIFICATIONS: {
        CREATE: [PERMISSIONS.PRODUCT_SPECIFICATION_WRITE],
        READ: [PERMISSIONS.PRODUCT_SPECIFICATION_READ],
        UPDATE: [PERMISSIONS.PRODUCT_SPECIFICATION_WRITE],
        REVIEW: [PERMISSIONS.PRODUCT_SPECIFICATION_REVIEW],
        APPROVE: [PERMISSIONS.PRODUCT_SPECIFICATION_APPROVE],
        PUBLISH: [PERMISSIONS.PRODUCT_SPECIFICATION_PUBLISH],
        EXPORT: [PERMISSIONS.PRODUCT_SPECIFICATION_EXPORT],
        MANAGE_SCHEMA: [PERMISSIONS.PRODUCT_SPECIFICATION_MANAGE_SCHEMA],
        MANAGE_PDF_TEMPLATES: [PERMISSIONS.PRODUCT_SPECIFICATION_MANAGE_PDF_TEMPLATES],
        MANAGE_SETTINGS: [PERMISSIONS.PRODUCT_SPECIFICATION_MANAGE_SETTINGS],
    },
    FEEDBACK: {
        MANAGE: [PERMISSIONS.FEEDBACK_MANAGE],
    },
    PRODUCT_DEVELOPMENT: {
        READ: [PERMISSIONS.PRODUCT_DEVELOPMENT_READ],
        UPDATE: [PERMISSIONS.PRODUCT_DEVELOPMENT_WRITE],
    },
    RAW_MATERIAL: {
        READ: [PERMISSIONS.RAW_MATERIAL_READ],
        UPDATE: [PERMISSIONS.RAW_MATERIAL_WRITE],
        APPROVE: [PERMISSIONS.RAW_MATERIAL_READ, PERMISSIONS.RAW_MATERIAL_APPROVE],
    },
    RECIPES: {
        CREATE: [PERMISSIONS.RECIPE_WRITE],
        READ: [PERMISSIONS.RECIPE_READ],
        UPDATE: [PERMISSIONS.RECIPE_WRITE],
        REVIEW: [PERMISSIONS.RECIPE_REVIEW],
        APPROVE: [PERMISSIONS.RECIPE_APPROVE],
        ARCHIVE: [PERMISSIONS.RECIPE_ARCHIVE],
        VIEW_COSTS: [PERMISSIONS.RECIPE_COSTS],
    },
    DECLARATIONS: {
        CREATE: [PERMISSIONS.DECLARATION_WRITE],
        READ: [PERMISSIONS.DECLARATION_READ],
        UPDATE: [PERMISSIONS.DECLARATION_WRITE],
        REVIEW: [PERMISSIONS.DECLARATION_REVIEW],
        APPROVE: [PERMISSIONS.DECLARATION_APPROVE],
        PUBLISH: [PERMISSIONS.DECLARATION_PUBLISH],
        MANAGE: [PERMISSIONS.DECLARATION_MANAGE_CATALOG],
        ARCHIVE: [PERMISSIONS.DECLARATION_APPROVE],
    },
    ANALYSIS: {
        CREATE: [PERMISSIONS.ANALYSIS_WRITE],
        READ: [PERMISSIONS.ANALYSIS_READ],
        UPDATE: [PERMISSIONS.ANALYSIS_WRITE],
        REVIEW: [PERMISSIONS.ANALYSIS_REVIEW],
        APPROVE: [PERMISSIONS.ANALYSIS_APPROVE],
        MANAGE: [PERMISSIONS.ANALYSIS_MANAGE_PARAMETERS],
        ARCHIVE: [PERMISSIONS.ANALYSIS_APPROVE],
    },
    MANUFACTURING: {
        CREATE: [PERMISSIONS.MANUFACTURING_WRITE],
        READ: [PERMISSIONS.MANUFACTURING_READ],
        UPDATE: [PERMISSIONS.MANUFACTURING_WRITE],
        RELEASE: [PERMISSIONS.MANUFACTURING_RELEASE],
        RESERVE: [PERMISSIONS.MANUFACTURING_RESERVE],
        CONSUME: [PERMISSIONS.MANUFACTURING_CONSUME],
        COMPLETE: [PERMISSIONS.MANUFACTURING_COMPLETE],
        VIEW_COSTS: [PERMISSIONS.MANUFACTURING_COSTS],
    },
    TRACEABILITY: {
        READ: [PERMISSIONS.TRACEABILITY_READ],
        SIMULATE: [PERMISSIONS.TRACEABILITY_SIMULATE],
        BLOCK: [PERMISSIONS.TRACEABILITY_BLOCK],
        EXPORT: [PERMISSIONS.TRACEABILITY_EXPORT],
    },
    MRP: {
        READ: [PERMISSIONS.MRP_READ],
        RUN: [PERMISSIONS.MRP_RUN],
        CONFIGURE: [PERMISSIONS.MRP_CONFIGURE],
        CONVERT_PURCHASE: [PERMISSIONS.MRP_CONVERT_PURCHASE],
        CONVERT_PRODUCTION: [PERMISSIONS.MRP_CONVERT_PRODUCTION],
    },
    LABEL_MANAGEMENT: {
        CREATE: [PERMISSIONS.LABEL_WRITE],
        READ: [PERMISSIONS.LABEL_READ],
        UPDATE: [PERMISSIONS.LABEL_WRITE],
        REVIEW: [PERMISSIONS.LABEL_WRITE],
        APPROVE: [PERMISSIONS.LABEL_APPROVE],
        EXPORT: [PERMISSIONS.LABEL_PRINT],
        ARCHIVE: [PERMISSIONS.LABEL_APPROVE],
    },
    CHANGE_CONTROL: {
        CREATE: [PERMISSIONS.CHANGE_CONTROL_WRITE],
        READ: [PERMISSIONS.CHANGE_CONTROL_READ],
        UPDATE: [PERMISSIONS.CHANGE_CONTROL_WRITE],
        REVIEW: [PERMISSIONS.CHANGE_CONTROL_REVIEW],
        APPROVE: [PERMISSIONS.CHANGE_CONTROL_APPROVE],
        ARCHIVE: [PERMISSIONS.CHANGE_CONTROL_APPROVE],
    },
    NEWSLETTER: {
        READ: [PERMISSIONS.NEWSLETTER_READ],
        UPDATE: [PERMISSIONS.NEWSLETTER_READ, PERMISSIONS.NEWSLETTER_WRITE],
        MANAGE: [PERMISSIONS.NEWSLETTER_READ, PERMISSIONS.NEWSLETTER_MANAGE_RECIPIENTS],
        APPROVE: [PERMISSIONS.NEWSLETTER_READ, PERMISSIONS.NEWSLETTER_MANAGE_CONSENT],
        EXPORT: [PERMISSIONS.NEWSLETTER_READ, PERMISSIONS.NEWSLETTER_EXPORT_CONSENT],
        MANAGE_SETTINGS: [PERMISSIONS.NEWSLETTER_READ, PERMISSIONS.NEWSLETTER_MANAGE_SETTINGS],
    },
    CUSTOM_FIELDS: {
        READ: [PERMISSIONS.CUSTOM_FIELDS_READ],
        EXECUTE: [PERMISSIONS.CUSTOM_FIELDS_READ, PERMISSIONS.TRANSLATIONS_GENERATE],
        REVIEW: [PERMISSIONS.CUSTOM_FIELDS_READ, PERMISSIONS.TRANSLATIONS_REVIEW],
        PUBLISH: [PERMISSIONS.CUSTOM_FIELDS_READ, PERMISSIONS.CUSTOM_FIELDS_PUBLISH],
        IMPORT: [PERMISSIONS.CUSTOM_FIELDS_READ, PERMISSIONS.CUSTOM_FIELDS_IMPORT],
        ROLLBACK: [PERMISSIONS.CUSTOM_FIELDS_READ, PERMISSIONS.CUSTOM_FIELDS_ROLLBACK],
        MANAGE_FIELDS: [PERMISSIONS.CUSTOM_FIELDS_READ, PERMISSIONS.CUSTOM_FIELDS_EDIT],
        MANAGE_GROUPS: [PERMISSIONS.CUSTOM_FIELDS_READ, PERMISSIONS.CUSTOM_FIELD_GROUPS_MANAGE],
        READ_CONFIDENTIAL: [
            PERMISSIONS.CUSTOM_FIELDS_READ,
            PERMISSIONS.CUSTOM_FIELDS_READ_CONFIDENTIAL,
        ],
        WRITE_CONFIDENTIAL: [
            PERMISSIONS.CUSTOM_FIELDS_READ,
            PERMISSIONS.CUSTOM_FIELDS_READ_CONFIDENTIAL,
            PERMISSIONS.CUSTOM_FIELDS_WRITE_CONFIDENTIAL,
        ],
    },
    TRANSLATIONS: {
        READ: [PERMISSIONS.CUSTOM_FIELDS_READ],
        EXECUTE: [PERMISSIONS.TRANSLATIONS_GENERATE],
        REVIEW: [PERMISSIONS.TRANSLATIONS_REVIEW],
        MANAGE: [PERMISSIONS.TRANSLATIONS_GLOSSARY_MANAGE],
    },
    SETUP: {
        READ: [PERMISSIONS.SETUP_READ],
        UPDATE: [PERMISSIONS.SETUP_READ, PERMISSIONS.SETUP_MANAGE],
        APPROVE: [PERMISSIONS.SETUP_READ, PERMISSIONS.SETUP_MANAGE, PERMISSIONS.SETUP_RELEASE],
    },
    IMPORTS: {
        READ: [PERMISSIONS.IMPORTS_READ],
        UPDATE: [PERMISSIONS.IMPORTS_READ, PERMISSIONS.IMPORTS_MANAGE],
        EXECUTE: [PERMISSIONS.IMPORTS_READ, PERMISSIONS.IMPORTS_MANAGE, PERMISSIONS.IMPORTS_EXECUTE],
        DELETE: [PERMISSIONS.IMPORTS_READ, PERMISSIONS.IMPORTS_ROLLBACK],
    },
    BANKING: {
        READ: [PERMISSIONS.BANKING_READ],
        CREATE: [PERMISSIONS.BANKING_READ, PERMISSIONS.BANKING_CONNECT],
        UPDATE: [PERMISSIONS.BANKING_READ, PERMISSIONS.BANKING_RECONCILE],
        DELETE: [PERMISSIONS.BANKING_READ, PERMISSIONS.BANKING_DISCONNECT],
        EXECUTE: [PERMISSIONS.BANKING_READ, PERMISSIONS.BANKING_SYNC],
        APPROVE: [PERMISSIONS.BANKING_READ, PERMISSIONS.BANKING_POST],
        RELEASE: [PERMISSIONS.BANKING_READ, PERMISSIONS.BANKING_AUTO_POST],
        ARCHIVE: [PERMISSIONS.BANKING_READ, PERMISSIONS.BANKING_REVERSE],
        READ_CONFIDENTIAL: [PERMISSIONS.BANKING_READ, PERMISSIONS.BANKING_READ_SENSITIVE],
        MANAGE_SETTINGS: [PERMISSIONS.BANKING_READ, PERMISSIONS.BANKING_MANAGE_SETTINGS],
    },
    SALES_ASSISTANT: {
        USE: [PERMISSIONS.SALES_ASSISTANT_USE],
        DELETE: [PERMISSIONS.SALES_ASSISTANT_DELETE],
        VIEW_OWN_TASKS: [PERMISSIONS.SALES_TASKS_VIEW_OWN],
        VIEW_TEAM_TASKS: [PERMISSIONS.SALES_TASKS_VIEW_OWN, PERMISSIONS.SALES_TASKS_VIEW_TEAM],
        ASSIGN: [PERMISSIONS.SALES_TASKS_ASSIGN],
        DOCUMENT_CONTACT: [PERMISSIONS.SALES_CONTACTS_DOCUMENT],
        SNOOZE: [PERMISSIONS.SALES_RECOMMENDATIONS_SNOOZE],
        EXCLUDE: [PERMISSIONS.SALES_RECOMMENDATIONS_EXCLUDE],
        GENERATE_AI: [PERMISSIONS.SALES_AI_SUMMARIES_CREATE],
    },
    SALES_RULES: {
        READ: [PERMISSIONS.SALES_RULES_VIEW],
        MANAGE: [PERMISSIONS.SALES_RULES_VIEW, PERMISSIONS.SALES_RULES_MANAGE],
        SIMULATE: [PERMISSIONS.SALES_RULES_VIEW, PERMISSIONS.SALES_RULES_SIMULATE],
    },
    SALES_ANALYTICS: {
        VIEW_TEAM: [PERMISSIONS.SALES_ANALYTICS_TEAM_VIEW],
        VIEW_EMPLOYEE_DETAILS: [PERMISSIONS.SALES_ANALYTICS_EMPLOYEE_VIEW],
    },
    CONTACT_PREFERENCES: {
        MANAGE: [PERMISSIONS.CONTACT_PREFERENCES_MANAGE],
    },
    BACKUPS: {
        READ: [PERMISSIONS.BACKUP_VIEW],
        CREATE_SETTINGS: [PERMISSIONS.BACKUP_VIEW, PERMISSIONS.BACKUP_CREATE_SETTINGS],
        CREATE_MASTER_DATA: [PERMISSIONS.BACKUP_VIEW, PERMISSIONS.BACKUP_CREATE_MASTER_DATA],
        CREATE_FULL: [PERMISSIONS.BACKUP_VIEW, PERMISSIONS.BACKUP_CREATE_FULL],
        DOWNLOAD: [PERMISSIONS.BACKUP_VIEW, PERMISSIONS.BACKUP_DOWNLOAD],
        DELETE: [PERMISSIONS.BACKUP_VIEW, PERMISSIONS.BACKUP_DELETE],
        RESTORE_DRY_RUN: [PERMISSIONS.BACKUP_VIEW, PERMISSIONS.BACKUP_RESTORE_DRY_RUN],
        RESTORE: [
            PERMISSIONS.BACKUP_VIEW,
            PERMISSIONS.BACKUP_RESTORE_DRY_RUN,
            PERMISSIONS.BACKUP_RESTORE,
        ],
        MANAGE_SETTINGS: [PERMISSIONS.BACKUP_VIEW, PERMISSIONS.BACKUP_MANAGE_SETTINGS],
        VIEW_DEPLOYMENT: [PERMISSIONS.BACKUP_VIEW, PERMISSIONS.BACKUP_DEPLOYMENT_VIEW],
    },
};
/** Nur belegte Zellen sind in der Oberfläche auswählbar. */
export function isMatrixCellAvailable(resource, action) {
    return MATRIX_TO_PERMISSIONS[resource][action] !== undefined;
}
export function availableMatrixEntries() {
    return PERMISSION_RESOURCES.flatMap((resource) => PERMISSION_ACTIONS.filter((action) => isMatrixCellAvailable(resource, action)).map((action) => ({ resource, action })));
}
/** Berechnet den Permission-Cache einer Rolle aus ihrer Matrix. */
export function permissionsForMatrix(entries) {
    const permissions = new Set();
    for (const entry of entries) {
        for (const permission of MATRIX_TO_PERMISSIONS[entry.resource][entry.action] ?? []) {
            permissions.add(permission);
        }
    }
    return [...permissions].sort();
}
/**
 * Rückabbildung bestehender Permission-Strings auf die Matrix. Eine Zelle gilt als
 * gesetzt, wenn alle ihr zugeordneten Permissions vorhanden sind.
 */
export function matrixForPermissions(permissions) {
    const granted = new Set(permissions);
    return availableMatrixEntries().filter((entry) => {
        const required = MATRIX_TO_PERMISSIONS[entry.resource][entry.action] ?? [];
        return required.length > 0 && required.every((permission) => granted.has(permission));
    });
}
/** Rollen mit Vollzugriff; sie umgehen die Matrixprüfung. */
export const SUPER_ADMIN_ROLE_KEYS = ['OWNER'];
export function isSuperAdminRole(roleKey) {
    return SUPER_ADMIN_ROLE_KEYS.includes(roleKey);
}
//# sourceMappingURL=permission-matrix.js.map